/**
 * REST client for AI authoring (B8). SERVER-ONLY data (no PowerSync):
 * proposals are server-authoritative — the budget, the sealed
 * credential and the antifraud checks live on the server, and exposing them to sync would expose
 * them to a modified client.
 *
 * Mirror of `apps/server/src/handlers/course_author.rs`'s DTOs.
 */
import { createSafeQuery } from "@/lib/api";
import { createMutation } from "@tanstack/solid-query";
import type {
  AiAuthoringAvailability,
  AiItemDecision,
  AiProposalCreated,
  AiProposalDetail,
  AiProposalSummary,
} from "@space/shared";
import { apiJson } from "@/lib/rest";

const BASE = "/api/course-author";

export interface ProposeInput {
  org?: string;
  prompt: string;
  /**
   * `CourseModuleRegistry.kinds()` ∩ `course_settings.enabled_module_kinds`.
   * Sent by the CLIENT because the registry is TypeScript: if the server
   * wired them, registering a new plugin would leave the AI unable to propose it.
   */
  allowedModuleKinds: string[];
  allowedBlockTypes: string[];
  courseId?: string;
  lessonId?: string;
  courseTitle?: string;
  lessonTitle?: string;
}

export interface ProposeConfigInput {
  org?: string;
  prompt: string;
  /** `rubric` | `session_plan`. */
  configKind: string;
  courseId?: string;
}

const keys = {
  availability: (org?: string, courseId?: string) =>
    ["ai-authoring", "availability", org ?? "", courseId ?? ""] as const,
  proposals: (org?: string, courseId?: string) =>
    ["ai-authoring", "proposals", org ?? "", courseId ?? ""] as const,
  proposal: (id: string) => ["ai-authoring", "proposal", id] as const,
};

export const useAiAuthoringAvailability = (
  org: () => string | undefined,
  courseId: () => string | undefined,
) =>
  createSafeQuery(() => ({
    queryKey: keys.availability(org(), courseId()),
    queryFn: () => {
      const params = new URLSearchParams();
      const o = org();
      if (o) {
        params.set("org", o);
      }
      const c = courseId();
      if (c) {
        params.set("courseId", c);
      }
      const qs = params.toString();
      return apiJson<AiAuthoringAvailability>(`${BASE}/availability${qs ? `?${qs}` : ""}`);
    },
    staleTime: 30_000,
    retry: false,
  }));

export const useAiProposals = (org: () => string | undefined, courseId: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: keys.proposals(org(), courseId()),
    queryFn: () => {
      const params = new URLSearchParams();
      const o = org();
      if (o) {
        params.set("org", o);
      }
      const c = courseId();
      if (c) {
        params.set("courseId", c);
      }
      const qs = params.toString();
      return apiJson<AiProposalSummary[]>(`${BASE}/proposals${qs ? `?${qs}` : ""}`);
    },
    staleTime: 5000,
    retry: false,
  }));

export const useAiProposal = (id: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: keys.proposal(id() ?? ""),
    queryFn: () => apiJson<AiProposalDetail>(`${BASE}/proposals/${id() ?? ""}`),
    get enabled() {
      return Boolean(id());
    },
    retry: false,
  }));

const post = <T>(path: string, body: unknown) =>
  apiJson<T>(`${BASE}${path}`, { method: "POST", body: JSON.stringify(body) });

export const useProposeStructure = () =>
  createMutation(() => ({
    mutationFn: (input: ProposeInput) => post<AiProposalCreated>("/propose/structure", input),
  }));

export const useProposeBlocks = () =>
  createMutation(() => ({
    mutationFn: (input: ProposeInput) => post<AiProposalCreated>("/propose/blocks", input),
  }));

export const useProposeEdit = () =>
  createMutation(() => ({
    mutationFn: (input: ProposeInput) => post<AiProposalCreated>("/propose/edit", input),
  }));

export const useProposeConfig = () =>
  createMutation(() => ({
    mutationFn: (input: ProposeConfigInput) => post<AiProposalCreated>("/propose/config", input),
  }));

/**
 * Records the reviewer's decisions. The server re-checks `valid`:
 * this verdict is produced by `validateProposalItem` cross-checking the two registries,
 * but the real barrier is in SQL (`AND valid`), not in this call.
 */
export const useDecideItems = () =>
  createMutation(() => ({
    mutationFn: (input: { proposalId: string; decisions: AiItemDecision[] }) =>
      post<{ changed: number; status: string }>(`/proposals/${input.proposalId}/decide`, {
        decisions: input.decisions,
      }),
  }));

export const useDeleteProposal = () =>
  createMutation(() => ({
    mutationFn: (id: string) =>
      apiJson<{ ok: boolean }>(`${BASE}/proposals/${id}`, { method: "DELETE" }),
  }));
